import type { OrderType } from '../types';

export const OWN_STOCK_LABEL = 'Own stock';

export const OWN_STOCK_CARD_TEXT = 'Own stock. No counterparty on this side.';

export type InventorySide = 'stock_in' | 'stock_out';

type TicketRef = { readonly id: string } | null;

// The list response carries each side's ticket ref under `detail`; the detail
// response carries it under `buy.ticket` / `sell.ticket`. Accepting all three
// shapes lets every surface call `inventorySide(order)` on its own order
// object, with no per-call adapter.
export interface InventorySideOrder {
  readonly orderType: OrderType;
  readonly buyTicket: TicketRef;
  readonly sellTicket: TicketRef;
}

interface InventorySideDetailOrder {
  readonly orderType: OrderType;
  readonly buy: { readonly ticket: TicketRef };
  readonly sell: { readonly ticket: TicketRef };
}

interface InventorySideListOrder {
  readonly orderType: OrderType;
  readonly detail: { readonly buyTicket: TicketRef; readonly sellTicket: TicketRef };
}

export type InventorySideInput =
  | InventorySideOrder
  | InventorySideDetailOrder
  | InventorySideListOrder;

function resolveTickets(order: InventorySideInput): { buy: TicketRef; sell: TicketRef } {
  if ('buy' in order) return { buy: order.buy.ticket, sell: order.sell.ticket };
  if ('detail' in order) return { buy: order.detail.buyTicket, sell: order.detail.sellTicket };
  return { buy: order.buyTicket, sell: order.sellTicket };
}

export function inventorySide(order: InventorySideInput): InventorySide | null {
  if (order.orderType !== 'Inventory') return null;
  const { buy, sell } = resolveTickets(order);
  if (buy !== null && sell === null) return 'stock_in';
  if (sell !== null && buy === null) return 'stock_out';
  return null;
}

export interface TicketPresence {
  readonly buy: boolean;
  readonly sell: boolean;
}

// Per-side presence, independent of direction. `inventorySide` collapses both
// a zero-sided and a two-sided Inventory order to null, so a caller that must
// tell those two cases apart (the counterparties line, the Documents section,
// the trader pickers) reads ticket presence directly instead.
export function ticketPresence(order: InventorySideInput): TicketPresence {
  const { buy, sell } = resolveTickets(order);
  return { buy: buy !== null, sell: sell !== null };
}
